/**
 * Listendruck als PDF.
 *
 * Gedruckt wird über den Browser: Er bringt die PDF-Ausgabe bereits mit
 * („Als PDF speichern“), kennt Papierformat und Ränder des Anwenders und
 * stellt Umlaute korrekt dar. Eine serverseitige PDF-Bibliothek nur für
 * Bestandslisten hinzuzunehmen hieße, all das nachzubauen.
 *
 * Gedruckt wird in einem eigenen Fenster statt über eine Druck-Regel auf der
 * Anwendungsseite: Die Liste erscheint dann ohne Menü, Filterleiste und
 * Schaltflächen, und der Anwender sieht in der Vorschau genau das Blatt, das
 * aus dem Drucker kommt.
 *
 * Der Auftragsbericht entsteht dagegen serverseitig – er wird auch versendet,
 * wenn niemand vor dem Bildschirm sitzt.
 */

export interface PrintColumn<T> {
  label: string;
  /** Zellinhalt. Wird als Text eingesetzt, nicht als HTML. */
  value: (row: T) => string | number | null | undefined;
  align?: 'left' | 'right' | 'center';
  /** Hebt die Zeile hervor, z. B. Unterschreitungen des Mindestbestands. */
  emphasis?: (row: T) => 'danger' | 'warning' | 'muted' | null;
}

export interface PrintOptions<T> {
  title: string;
  subtitle?: string;
  /** Kopfzeilen-Angaben wie Mandant, Lager oder Stichtag. */
  meta?: { label: string; value: string }[];
  columns: PrintColumn<T>[];
  rows: T[];
  /** Abschließende Zeile, etwa Summen. */
  footer?: { label: string; value: string }[];
  /** Unterschriftsfelder – für Inventurlisten, die abgezeichnet werden. */
  signatures?: string[];
  /** Zusätzlicher Text unter der Tabelle. */
  note?: string;
  orientation?: 'portrait' | 'landscape';
}

/**
 * Menge mit Einheit für den Ausdruck.
 *
 * Ganze Stückzahlen ohne Nachkommastellen: „52 Stk“ statt „52,00 Stk“. Gebinde
 * wie 2,5 Liter behalten ihre Nachkommastellen – nur bei ihnen tragen sie
 * Information.
 */
export function printQuantity(value: number, unit?: string): string {
  const formatted = value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return unit ? `${formatted} ${unit}` : formatted;
}

/** Maskiert Text, bevor er ins Druckdokument eingesetzt wird. */
function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const printStyles = (orientation: 'portrait' | 'landscape') => `
  @page { size: A4 ${orientation}; margin: 14mm 12mm 16mm; }
  * { box-sizing: border-box; }
  body {
    font-family: "Segoe UI", system-ui, -apple-system, sans-serif;
    font-size: 10pt; color: #14181f; margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  header { display: flex; align-items: flex-start; gap: 14px; border-bottom: 2px solid #1f6f5c; padding-bottom: 10px; }
  header img { height: 34px; }
  header .titles { flex: 1; }
  h1 { font-size: 15pt; margin: 0 0 2px; }
  .subtitle { color: #55606f; font-size: 9.5pt; margin: 0; }
  .printed { text-align: right; font-size: 8.5pt; color: #55606f; white-space: nowrap; }
  .meta { display: flex; flex-wrap: wrap; gap: 6px 22px; margin: 10px 0 12px; font-size: 9pt; }
  .meta div { display: flex; gap: 5px; }
  .meta span.label { color: #55606f; }
  .meta span.value { font-weight: 600; }
  table { width: 100%; border-collapse: collapse; }
  thead { display: table-header-group; }  /* Kopfzeile auf jeder Seite wiederholen */
  tr { page-break-inside: avoid; }
  th, td { padding: 4px 6px; border-bottom: 1px solid #dfe4ea; text-align: left; vertical-align: top; }
  th { background: #eef4f2; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.04em; color: #33404f; border-bottom: 1.5px solid #1f6f5c; }
  td.right, th.right { text-align: right; }
  td.center, th.center { text-align: center; }
  tbody tr:nth-child(even) { background: #f8fafb; }
  tr.danger td { background: #fdeceb; color: #8d1f16; }
  tr.warning td { background: #fdf5e4; color: #7a5411; }
  tr.muted td { color: #6b7686; }
  tfoot td { border-top: 1.5px solid #1f6f5c; border-bottom: none; font-weight: 700; padding-top: 7px; }
  .note { margin-top: 12px; font-size: 9pt; color: #55606f; white-space: pre-wrap; }
  .signatures { display: flex; gap: 34px; margin-top: 30px; page-break-inside: avoid; }
  .signatures div { flex: 1; border-top: 1px solid #14181f; padding-top: 4px; font-size: 8.5pt; color: #55606f; }
  .empty { padding: 22px; text-align: center; color: #6b7686; font-style: italic; }
`;

export function printList<T>(options: PrintOptions<T>): void {
  const orientation = options.orientation ?? 'portrait';
  const printedAt = new Date().toLocaleString('de-DE', { dateStyle: 'long', timeStyle: 'short' });

  const head = options.columns
    .map((column) => `<th class="${column.align === 'right' ? 'right' : column.align === 'center' ? 'center' : ''}">${escapeHtml(column.label)}</th>`)
    .join('');

  const body = options.rows.length
    ? options.rows
        .map((row) => {
          const emphasis = options.columns.map((column) => column.emphasis?.(row)).find(Boolean) ?? '';
          const cells = options.columns
            .map(
              (column) =>
                `<td class="${column.align === 'right' ? 'right' : column.align === 'center' ? 'center' : ''}">${escapeHtml(column.value(row))}</td>`,
            )
            .join('');
          return `<tr class="${emphasis}">${cells}</tr>`;
        })
        .join('')
    : `<tr><td class="empty" colspan="${options.columns.length}">Keine Einträge</td></tr>`;

  const footer = options.footer?.length
    ? `<tfoot><tr><td colspan="${Math.max(1, options.columns.length - options.footer.length)}">${escapeHtml(
        options.footer[0].label,
      )}</td>${options.footer
        .map((entry, index) => `<td class="right">${escapeHtml(index === 0 ? entry.value : `${entry.label}: ${entry.value}`)}</td>`)
        .join('')}</tr></tfoot>`
    : '';

  const html = `<!doctype html>
<html lang="de">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(options.title)}</title>
  <style>${printStyles(orientation)}</style>
</head>
<body>
  <header>
    <img src="${window.location.origin}/logo.png" alt="" onerror="this.remove()" />
    <div class="titles">
      <h1>${escapeHtml(options.title)}</h1>
      ${options.subtitle ? `<p class="subtitle">${escapeHtml(options.subtitle)}</p>` : ''}
    </div>
    <div class="printed">Ausgedruckt am<br />${escapeHtml(printedAt)}</div>
  </header>
  ${
    options.meta?.length
      ? `<div class="meta">${options.meta
          .map((entry) => `<div><span class="label">${escapeHtml(entry.label)}:</span><span class="value">${escapeHtml(entry.value)}</span></div>`)
          .join('')}</div>`
      : ''
  }
  <table>
    <thead><tr>${head}</tr></thead>
    <tbody>${body}</tbody>
    ${footer}
  </table>
  ${options.note ? `<p class="note">${escapeHtml(options.note)}</p>` : ''}
  ${
    options.signatures?.length
      ? `<div class="signatures">${options.signatures.map((label) => `<div>${escapeHtml(label)}</div>`).join('')}</div>`
      : ''
  }
</body>
</html>`;

  const printWindow = window.open('', '_blank', 'width=1024,height=768');
  if (!printWindow) {
    alert('Der Druck wurde vom Browser blockiert. Bitte Pop-ups für diese Seite erlauben.');
    return;
  }

  printWindow.document.write(html);
  printWindow.document.close();

  // Erst drucken, wenn das Logo geladen ist – sonst bleibt im Ausdruck eine
  // Lücke, wo es hingehört.
  const start = () => {
    printWindow.focus();
    printWindow.print();
  };
  if (printWindow.document.readyState === 'complete') start();
  else printWindow.addEventListener('load', start);
}
